import { approveOffer, saveCircularIncentives, savePageIncentives, deleteOffer } from "api";
import { omit } from "lodash";
import {checkMinQtyDollarF, offerValidationSchema, standardizeText, validateIncentive, validateOffer, approveOfferRestrictions, approveOfferValidations, parseIncentive, getIncentiveInfo, checkDupeOfferEntry, maxIdenticalOfferEntries, MAX_IDENTICAL_SPLIT_ENTRIES, computeCouponFlags, isCouponAutoCheckExcluded } from "utils";

export const createOfferSlice = (set, get) => ({
    offers: [],
    incentives: [],
    currentItemType: "offer", // or "incentive",
    isApproved: false,
    setCurrentItemType: (type) => set({ currentItemType: type }),
    addNewOffer: () => {
        const offer = {
            index: get().offers.length,
            isValid: false,
            include_in_net_cost_f: true,
            offer_type: "",
            offer_text: "",
            reg_price: "",
            reg_price2_d: "",
            limit: "",
            min_qty_dollar_f: false,
            min_qty: "",
            must_buy_f: false,
            qty_off_total_purchase_f: false,
            mix_and_match_f: false,
            liner_f: false,
        }
        set((state) => ({
            offers: [...state.offers, offer],
            currentOffer: offer,
            currentItemType: "offer",
            incentives: state.incentives.map(incentive => ({
                ...incentive,
                index: incentive.index + 1
            })),
        }))
    },
    addNewIncentive: () => {
        const incentive = {
            index: get().offers.length + get().incentives.length,
            isValid: false,
            isAddedIncentive: true,
            incentive_type: "",
            incentive_attach_to: "Adblock",
            incentive_text: "",
            incentive_limit: "",
            incentive_include_net_cost: true,
            incentive_by_mail: false,
            incentive_online_incentive: false,
            incentive_liner_f: false
        }
        set((state) => ({
            incentives: [...state.incentives, incentive],
            currentOffer: incentive,
            currentItemType: "incentive"
        }))
    },
    addOffers: (offers) => set((state) => ({
        offers: [...state.offers, ...offers]
    })),
    cloneOffer: (offerIdx) => {
        const offer = get().offers[offerIdx];
        if (offer) {
            const clonedOffer = { 
                ...offer, 
                id: null, 
                index: get().offers.length, 
                uuid: undefined,
                existingOffer: false,
            };
            set((state) => ({
                offers: [...state.offers, clonedOffer],
                currentOffer: clonedOffer,
                incentives: state.incentives.map(incentive => ({
                    ...incentive,
                    index: incentive.index + 1
                })),
            }));
            get().syncCouponFlags();
        }
    },
    addIncentives: (incentives) => set((state) => ({
        incentives: [...state.incentives, ...incentives]
    })),
    cloneIncentive: (incentiveIdx) => {
        const incentive = get().incentives[incentiveIdx];
        if (incentive) {
            const clonedIncentive = {
                ...incentive,
                index: get().offers.length + get().incentives.length,
                id: null, uuid: undefined, isClonedIncentive: true
            };
            set((state) => ({
                incentives: [...state.incentives, clonedIncentive],
                currentOffer: clonedIncentive
            }));
            get().syncCouponFlags();
        }
    },
    // removes local offer
    removeOffer: (offerIdx) => {
        set((state) => {
            const filteredOffers = state.offers
                .filter(offer => offer.index !== offerIdx)
                .map((offer, idx) => ({ ...offer, index: idx }));
            const updatedIncentives = state.incentives.map((incentive, idx) => ({
                ...incentive,
                index: filteredOffers.length + idx
            }));
            return {
                offers: filteredOffers,
                incentives: updatedIncentives,
                currentOffer: filteredOffers[0] || null
            };
        });
        get().syncCouponFlags();
    },
    // remove adblock/page/circular incentive locally, requires approval
    removeIncentive: (incentiveIdx) => {
        set((state) => {
            const filteredIncentives = state.incentives
                .filter(incentive => incentive.index !== incentiveIdx)
                .map((incentive, idx) => ({
                    ...incentive,
                    index: state.offers.length + idx
                }));
            return {
                incentives: filteredIncentives,
                currentOffer: state.offers[0] || null,
            };
        });
        get().syncCouponFlags();
    },
    // Remove Approved offer
    removeOfferByID: async (offerUUID) => {
        const res = await deleteOffer({
            adblockId: get().currentAdBlockID,
            offerId: offerUUID
        });
        if (res.success) {
            set((state) => {
                const newOffers = state.offers
                    .filter(offer => offer.uuid !== offerUUID)
                    .map((offer, idx) => ({ ...offer, index: idx }));
                const updatedIncentives = state.incentives.map((incentive, idx) => ({
                    ...incentive,
                    index: newOffers.length + idx
                }));
                let newCurrentOffer = state.currentOffer;
                if (state.currentOffer && state.currentOffer.uuid === offerUUID) {
                    newCurrentOffer = newOffers.length > 0 ? newOffers[0] : null;
                }
                return {
                    offers: newOffers,
                    incentives: updatedIncentives,
                    currentOffer: newCurrentOffer
                };
            });
            get().updateActiveBlockData({ offers_approved: false });
            console.log(get().offers);
        } else {
            console.error("Failed to delete offer:", res.error);
        }
    },
    updateOffer: (offerId, updatedData) => set((state) => ({
        offers: state.offers.map(offer =>
            offer.id === offerId ? { ...offer, ...updatedData } : offer
        ),
    })),
    updateCurrentOffer: (updatedData) => {
        const currentOffer = get().currentOffer;
        const offerText = updatedData.offer_text ?? currentOffer.offer_text;
        const { min_qty, min_qty_dollar_f } = checkMinQtyDollarF(offerText, currentOffer.offer_type);

        updatedData.min_qty = min_qty;
        updatedData.min_qty_dollar_f = min_qty_dollar_f;
        updatedData.isValid = validateOffer({ ...get().currentOffer, ...updatedData });

        set((state) => ({
            currentOffer: { ...state.currentOffer, ...updatedData },
            offers: state.offers.map(offer =>
                offer.index === state.currentOffer.index ? { ...offer, ...updatedData } : offer
            ),
        }))
        get().syncCouponFlags();
    },
    updateCurrentIncentive: (updatedData) => {
        updatedData.isValid = validateIncentive({ ...get().currentOffer, ...updatedData });
        set((state) => ({
            currentOffer: { ...state.currentOffer, ...updatedData },
            incentives: state.incentives.map(incentive =>
                incentive.index === state.currentOffer.index ? { ...incentive, ...updatedData } : incentive
            ),
        }))
        get().syncCouponFlags();
    },
    // Auto-manage the Coupon box (PEP-6516): disabled + cleared unless a coupon
    // signal exists (offer w/Coupon, Adblock coupon incentive, or coupon page /
    // digital coupon), in which case it is enabled and auto-checked. (edit view)
    syncCouponFlags: (isOnPageLoad, meta_coupon_f) => set((state) => {
        const couponContext =
            (state.activePageData?.pageLoc === "4" && !isCouponAutoCheckExcluded(state.activeBlockData?.retailer_name)) ||
            !!state.activePageData?.digital_coupon_f;
        const { coupon_f, coupon_amnt, couponDisabled } = computeCouponFlags({
            offers: state.offers,
            incentives: state.incentives,
            couponF: state.coupon_f,
            couponContext,
        });
        return {
            coupon_f: isOnPageLoad ? meta_coupon_f : coupon_f,
            coupon_amnt: coupon_f ? (coupon_amnt ?? '') : '',
            couponDisabled: isOnPageLoad ? false : couponDisabled, // enable coupon_f on page load(not checked)
        };
    }),
    // handleLoyaltyFlag : () =>  {
    //     set((state) => {
    //         const hasAdblockIncentive = state.incentives.some(
    //             (incentive) => incentive.incentive_attach_to === "Adblock"
    //         );

    //         return {
    //             currentOffer: state.currentOffer,
    //             incentives: state.incentives,
    //             ...(hasAdblockIncentive ? { loyalty_f: true } : {}),
    //         };
    //     });
    // },
    currentOffer: null,
    // currentIncentive: null,
    setCurrentOffer: (offer) => set({ currentOffer: offer }),
    // setCurrentIncentive: (incentive) => set({ currentIncentive: incentive }),
    initOfferIncentives: () => set({ offers: [], incentives: [], currentOffer: null, currentIncentive: null }),
    // initIncentives: () => set({ incentives: [], currentIncentive: null }),
    netUnitPriceValue: null,
    existingOffers: [],
    existingOfferInfo: null,
    approveOffer: async ({ skipValidation = false, netCostValues = null }) => {
        set({ isApproving: true })
        let offerData = get().offers;
        let incentiveData = get().incentives;
        const split = get().split;
        let invalidOffer = offerData.some(offer => !offer.isValid);
        if (invalidOffer) {
            set({ isApproving: false })
            return {
                success: false,
                message: "Some offers are invalid"
            };
        }
        
        offerData = offerData.map(offer => omit(offer, [
            'index',
            'isValid'
        ]))

        // Non-split blocks must not contain identical offer entries (the
        // backend would silently drop them); warn the operator instead.
        if (!split && checkDupeOfferEntry(offerData)) {
            set({ isApproving: false })
            return {
                success: false,
                message: "Duplicate offer entries detected. This is not a split adblock, please remove the duplicate offers and reapprove."
            };
        }

        // Split blocks legitimately allow duplicate entries, but a flood of
        // identical rows is accidental duplication (e.g. a held clone hotkey).
        if (split && maxIdenticalOfferEntries(offerData) > MAX_IDENTICAL_SPLIT_ENTRIES) {
            set({ isApproving: false })
            return {
                success: false,
                message: `More than ${MAX_IDENTICAL_SPLIT_ENTRIES} identical offer entries detected. This looks like accidental duplication, please remove the extra rows and reapprove.`
            };
        }

        for (const value of offerData) {
            try {
                await offerValidationSchema.validate(value, { abortEarly: false });
            } catch (err) {
                set({ isApproving: false })
                return {
                    success: false,
                    message: err.message || "Validation failed",
                };
            }
        }

        offerData.forEach((offer) => {
            let offerText = standardizeText(offer.offer_text);
            offer.offer_text = offerText;
        });

        const adblock = {
            ...get().getMetaData(),
            split: split ? 1 : 0,
            incentive_text: incentiveData.filter(
                (a) => validateIncentive(a) && a?.incentive_attach_to === "Adblock"
            )
                .map((a) => `${a?.incentive_liner_f ? `(L)`
                    : ''}${a?.incentive_type ? `(${a?.incentive_type}) `
                        : ''}${a.incentive_text ? standardizeText(a.incentive_text)
                            : ''} (${a?.incentive_limit ? `${a?.incentive_limit}`
                                : ""}) (${a?.incentive_include_net_cost ? `Net Cost`
                                    : ''}) (${a?.incentive_by_mail ? `1`
                                        : `0`}) (${a?.incentive_online_incentive ? `1`
                                            : `0`})`)
                .join(","),
        }

        let data = {
            adblock,
            offers: offerData,
            type: "edit",
        };

        const isImported = get().isImported;

        const endTime = new Date().toISOString().replace(',', '')

        if (!isImported) {
            data.adblock.ltr_offer_started_at = get().offerStartTime
            data.adblock.ltr_offer_finished_at = endTime
        } else {
            data.adblock.offer_import_started_at = get().offerStartTime
            data.adblock.offer_import_finished_at = endTime
        }

        if (!split) {
            const restrictions = approveOfferRestrictions({ offers: offerData, linerNetCost: netCostValues?.liner, nonLinerNetCost: netCostValues?.nonLiner });
            if (restrictions.error === true) {
                set({ isApproving: false })
                return {
                    success: false,
                    message: restrictions.message
                };
            }
        }

        const validations = approveOfferValidations({ offers: offerData, incentives: incentiveData, adblock: adblock });
        if (validations.error === true && !skipValidation) {
            set({ isApproving: false })
            return {
                success: false,
                canOverride: validations.canOverride,
                message: validations.message
            };
        }
        await saveCircularIncentives(
            get().circularUUID,
            incentiveData.filter(
                (a) => validateIncentive(a) && a?.incentive_attach_to === "Circular"
            )
        );
        await savePageIncentives(
            get().currentPageID,
            incentiveData.filter(
                (a) => validateIncentive(a) && a?.incentive_attach_to === "Page"
            )
        );
        const result = await approveOffer(get().currentAdBlockID, data);
        if (result.success) {
            get().updateAdblockField(get().currentAdBlockID, "products_approved", true);
            get().updateAdblockField(get().currentAdBlockID, "offers_approved", true);
            set({ isApproving: false, isImported: false });
            get().navigateNextUnapproved();
            return {
                success: true,
            }
        } else {
            if (result.error) {
                const errorMessage = result.error?.response?.data?.errors[0] || result.error?.message || "An error occurred while approving the offer";
                if (errorMessage && (
                    errorMessage.includes('value greater than 50% which is not allowed as per sub-category policy') ||
                    errorMessage.includes('liner offers entry has value greater than 50%') ||
                    errorMessage.includes('non-liner offers entry has value greater than 50%')
                )) {
                    // setLargeSaveOfferMessage(errorMessage);
                    // setLargeSaveOfferModalOpen(true);
                    // setIsSubmitting(false);
                    return {
                        success: false,
                        setLargeSaveOfferMessage: errorMessage
                    }
                }
            }
        }
    },
    handleImportOffer: async (data) => {
        const { offers, incentive_nm } = data.payload;
        const offersData = offers.map((item, index) => {
            delete item.uuid;
            return {
                ...item,
                offer_type: item.offer_type.toLowerCase() === 'price ea' ? 'Price ea' : item.offer_type,
                isValid: validateOffer(item),
                index,
            }
        });
        set({ offers: offersData, isImported: true });
        if(incentive_nm && incentive_nm !== '') {
            const incentive_data = parseIncentive(getIncentiveInfo(incentive_nm), 'Adblock', (get().offers.length + get().incentives.length));
            if(incentive_data.length > 0) {
                get().addIncentives(incentive_data);
            }
        }
    },
});
